import { acceptedOffer, latestAcceptedOffer, offerStandings, type HrWorkflowRecord, type HrWorkflowView, type OfferRecord, type Person } from '@vigeo/shared';

/**
 * What Offer and stage shows for a person, as ONE of these and never two (#440).
 *
 * A reading of `offerStandings`, `acceptedOffer` and the offer steps — nothing here is stored, so
 * there is no status to fall out of step with the record (hard rule 4).
 *
 * `legacy` is the honest name for everything the approved frames do not draw yet: a sent offer
 * waiting on a signature, an expired or withdrawn one, a paper-signed one, a sent-back one, and
 * more than one offer at once. Each of those is a case of the `switch` in `OfferStageStates` that
 * the companion design issue fills in when its frame is approved.
 */
export type OfferStageState =
  /** 2.1 / 2.2 / 2.5 — `OfferStageSection` answers for these and nothing else is drawn. */
  | { readonly kind: 'stage-section' }
  /** 945:13233 */
  | { readonly kind: 'no-offer' }
  /** 1.2 — `editing` is the draft being changed, or null for a new one. `from` seeds a new draft. */
  | { readonly kind: 'enter-offer'; readonly editing: OfferRecord | null; readonly from: OfferRecord | null }
  /** Draft saved, 970:13378 — the summary a single draft opens on. Review offer opens 1.3. */
  | { readonly kind: 'draft-saved'; readonly record: OfferRecord }
  /** Sent, waiting for the candidate, 970:13595. */
  | { readonly kind: 'sent-waiting'; readonly record: OfferRecord }
  /** Sent back, waiting to sign again, 971:13847. */
  | { readonly kind: 'sent-back'; readonly record: OfferRecord; readonly step: HrWorkflowView['offerSteps'][number] }
  /** Offer expired, 971:14266. */
  | { readonly kind: 'expired'; readonly record: OfferRecord }
  /** The paper-signed form, opened from "Record a paper-signed offer". */
  | { readonly kind: 'paper-offer' }
  /** 1.3 */
  | { readonly kind: 'review-offer'; readonly record: OfferRecord }
  /** 1.4 */
  | { readonly kind: 'send-offer'; readonly record: OfferRecord }
  /**
   * A person past Candidate with no offer in flight: the 2.5 outcome card stands alone and HR is
   * offered "Draft a new offer", seeded from the latest accepted offer's terms (`from`), or blank
   * for a hire with no offer on record.
   */
  | { readonly kind: 'past-candidate-idle'; readonly from: OfferRecord | null }
  | { readonly kind: 'legacy' };

/** What HR has asked to open. Not state about the offer — it is where HR just clicked. */
export type OfferStageRequest =
  | { readonly kind: 'draft'; readonly recordId: string | null; readonly fromRecordId?: string }
  | { readonly kind: 'review' }
  | { readonly kind: 'paper' }
  | null;

/**
 * The lifecycles that are an employment relationship — the ones a new offer (a raise, a change of
 * role, a redo) makes sense for. A separated or withdrawn person has no offer area at all.
 */
const employed = new Set<Person['lifecycle']>(['onboarding', 'active', 'inactive']);

export function offerStageState(
  person: Pick<Person, 'lifecycle'>,
  fullView: HrWorkflowView,
  now: string,
  requested: OfferStageRequest,
): OfferStageState {
  const pastCandidate = employed.has(person.lifecycle);
  if (person.lifecycle !== 'candidate' && !pastCandidate) return { kind: 'stage-section' };

  const allOffers = fullView.records.filter((record): record is OfferRecord | Extract<HrWorkflowRecord, { kind: 'existing-offer' }> => record.kind === 'offer' || record.kind === 'existing-offer');
  /*
   * PAST CANDIDATE: the accepted offers belong to 2.5 and stay out of this machine, which then runs
   * over every OTHER offer exactly as it does for a candidate. Which accepted offer is "the" offer
   * once a second one is signed is `latestAcceptedOffer`; every accepted one stays out of the machine.
   */
  const acceptedRecords = allOffers.filter((record) => acceptedOffer(record, fullView.offerSteps, fullView.candidacySince));
  const acceptedIds = new Set(acceptedRecords.map((record) => record.id));
  const view: HrWorkflowView = pastCandidate
    ? { ...fullView, records: fullView.records.filter((record) => !acceptedIds.has(record.id)) }
    : fullView;
  // The seed for "Draft a new offer": the same offer the 2.5 card reports. A paper-signed offer
  // carries no terms, so when that is the latest there is nothing to seed from.
  const theAccepted = latestAcceptedOffer(allOffers, fullView.offerSteps, fullView.candidacySince);
  const latestAccepted = theAccepted?.kind === 'offer' ? theAccepted : null;

  const offers = view.records.filter((record) => record.kind === 'offer' || record.kind === 'existing-offer');
  // The same two reads `OfferStageSection` makes to choose rows 2.1 and 2.2. A step the candidate
  // has signed and HR has not decided on is `submitted`; an offer past the ADR-011 §4 gate is accepted.
  const awaitingReview = offers.some((record) => record.kind === 'offer' && record.instanceId !== null
    && view.offerSteps.some((step) => step.id === record.instanceId && step.state === 'submitted'));
  const accepted = acceptedRecords.length > 0;
  if (awaitingReview || (!pastCandidate && accepted)) return { kind: 'stage-section' };

  if (requested?.kind === 'draft') {
    if (requested.recordId === null) {
      // A new draft that starts from an earlier offer's details (the Expired screen's "Draft a new offer").
      const from = requested.fromRecordId === undefined ? null
        : fullView.records.find((record): record is OfferRecord => record.kind === 'offer' && record.id === requested.fromRecordId) ?? null;
      return { kind: 'enter-offer', editing: null, from };
    }
    // Only a draft, or a reviewed offer that has not gone out, can be edited; a stale request for anything else is ignored rather than turned
    // into a second, blank draft.
    const editing = view.records.find((record): record is OfferRecord => record.kind === 'offer' && record.id === requested.recordId && (record.status === 'draft' || record.status === 'reviewed'));
    if (editing !== undefined) return { kind: 'enter-offer', editing, from: null };
  }
  if (requested?.kind === 'paper') return { kind: 'paper-offer' };

  const standings = offerStandings({ records: view.records, steps: view.offerSteps, candidacySince: view.candidacySince }, now);
  if (standings.every((standing) => standing.group === 'to-prepare')) return pastCandidate ? { kind: 'past-candidate-idle', from: latestAccepted } : { kind: 'no-offer' };

  const only = standings.length === 1 ? standings[0]! : null;
  if (only?.record?.kind === 'offer') {
    const record = only.record;
    if (only.group === 'drafts') return requested?.kind === 'review' ? { kind: 'review-offer', record } : { kind: 'draft-saved', record };
    if (only.group === 'ready-to-send') return { kind: 'send-offer', record };
    if (only.group === 'expired') return { kind: 'expired', record };
    if (only.group === 'out-for-signature') {
      const step = view.offerSteps.find((candidate) => candidate.id === record.instanceId);
      // `rejected` is a step HR sent back: it is with the candidate again, and still out for signature.
      return step?.state === 'rejected' ? { kind: 'sent-back', record, step } : { kind: 'sent-waiting', record };
    }
  }
  return { kind: 'legacy' };
}
